import React, {useEffect, useMemo, useRef, useState} from "react";
import {Container} from "react-bootstrap";
import {PiPause, PiPlay, PiCircleFill} from "react-icons/pi";
import NutzungLayout from "../../Components/Nutzung/NutzungLayout.jsx";
import useNutzungFilter from "../../Components/Nutzung/useNutzungFilter.js";
import useStatisticsData from "../../Components/Statistics/useStatisticsData.js";
import usePollingWhenVisible from "../../hooks/usePollingWhenVisible.js";
import {bereichFarbe, formatTimeDE} from "../../Components/Nutzung/format.js";
import {BereichLink, MitarbeiterLink, SeiteLink} from "../../Components/Nutzung/Links.jsx";
import NutzungWidget from "../../Components/Nutzung/NutzungWidget.jsx";

const URL = "/nutzung/daten/strom";
const TAKT_MS = 10000;
const HERVORHEBUNG_MS = 4000;
const OBERGRENZE = 300;

/** Kurzes, lokal begrenztes Hervorheben neu eingetroffener Zeilen - dieselbe
 * "nutzung-puls"-Idee wie im Rest der Auswertung, hier als sanftes
 * Ausblenden statt Dauerpulsieren, damit ein schneller Strom nicht
 * dauerhaft blinkt. */
const STYLE = `
@keyframes nutzungStromEinblenden{0%{background-color:rgba(47,158,68,.16)}100%{background-color:transparent}}
.nutzung-strom-neu{animation:nutzungStromEinblenden 4s ease-out}
`;

function baueUrl(filters, seit) {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            params.set(key, value);
        }
    });
    if (seit) {
        params.set("seit", seit);
    }
    const qs = params.toString();
    return qs ? `${URL}?${qs}` : URL;
}

/** Eindeutiger Schlüssel eines Ereignisses - die Spezifikation nennt für
 * diesen Endpunkt kein eigenes id-Feld, deshalb aus Zeit, Person, Art und
 * Seite zusammengesetzt. Für den seltenen Fall zweier Ereignisse derselben
 * Person zur exakt selben Sekunde reicht das, weil dann ohnehin beide
 * Zeilen gleich aussehen würden. */
function ereignisSchluessel(row) {
    return `${row.user_id ?? "sys"}|${row.zeit}|${row.art}|${row.seite}`;
}

/** Eine Zeile des Ereignisstroms. */
function StromZeile({row, neu}) {
    return (
        <div className={`d-flex align-items-center flex-wrap gap-3 py-2 border-bottom ${neu ? "nutzung-strom-neu" : ""}`}>
            <span className="small text-muted flex-shrink-0" style={{minWidth: 52, fontVariantNumeric: "tabular-nums"}}>
                {formatTimeDE(row.zeit)}
            </span>
            <span className="fw-semibold flex-shrink-0" style={{minWidth: 150}}>
                <MitarbeiterLink id={row.user_id} name={row.name} />
            </span>
            <span className="flex-grow-1" style={{minWidth: 160}}>
                {row.bezeichnung || row.art || "–"}
            </span>
            <span className="small" style={{minWidth: 140}}>
                <SeiteLink route={row.seite} />
            </span>
            <span
                className="badge fw-normal flex-shrink-0"
                style={{backgroundColor: `${bereichFarbe(row.bereich)}22`, color: bereichFarbe(row.bereich)}}
            >
                <BereichLink bereich={row.bereich} className="text-decoration-none" style={{color: "inherit"}} />
            </span>
        </div>
    );
}

/**
 * Fortlaufender Ereignisstrom aller Mitarbeiter - neueste Zeile oben, lädt
 * alle 10 Sekunden Hinzugekommenes nach (Parameter "seit"), pausiert bei
 * unsichtbarem Tab automatisch (usePollingWhenVisible) und zusätzlich per
 * Schalter, damit sich eine lange Liste in Ruhe lesen lässt.
 */
export default function NutzungStrom() {
    const nutzungFilters = useNutzungFilter();
    const {filters} = nutzungFilters;

    const [eintraege, setEintraege] = useState([]);
    const [neueSchluessel, setNeueSchluessel] = useState(() => new Set());
    const [seitCursor, setSeitCursor] = useState(null);
    const [pausiert, setPausiert] = useState(false);
    const gesehenRef = useRef(new Set());

    const initial = useStatisticsData(URL, filters);

    useEffect(() => {
        if (!initial.data) {
            return;
        }
        const rows = Array.isArray(initial.data) ? initial.data : [];
        gesehenRef.current = new Set(rows.map(ereignisSchluessel));
        setEintraege(rows);
        setNeueSchluessel(new Set());
        setSeitCursor(rows[0]?.zeit || null);
    }, [initial.data]);

    const pollUrl = useMemo(() => {
        if (pausiert || !seitCursor) {
            return null;
        }
        return baueUrl(filters, seitCursor);
    }, [pausiert, seitCursor, filters]);

    usePollingWhenVisible(pollUrl, TAKT_MS, (frisch) => {
        const neu = (Array.isArray(frisch) ? frisch : []).filter((row) => !gesehenRef.current.has(ereignisSchluessel(row)));
        if (neu.length === 0) {
            return;
        }
        const schluessel = neu.map(ereignisSchluessel);
        schluessel.forEach((key) => gesehenRef.current.add(key));

        setEintraege((bisherige) => [...neu, ...bisherige].slice(0, OBERGRENZE));
        setNeueSchluessel((vorher) => new Set([...vorher, ...schluessel]));
        setSeitCursor(neu[0]?.zeit || seitCursor);

        setTimeout(() => {
            setNeueSchluessel((vorher) => {
                const rest = new Set(vorher);
                schluessel.forEach((key) => rest.delete(key));
                return rest;
            });
        }, HERVORHEBUNG_MS);
    });

    return (
        <Container fluid className="pt-2 pb-4">
            <style>{STYLE}</style>
            <NutzungLayout gruppe="Jetzt" titel="Ereignisstrom" nutzungFilters={nutzungFilters}>
                <NutzungWidget
                    title="Ereignisstrom"
                    help="Alle Ereignisse aller Mitarbeiter, neueste oben. Lädt alle 10 Sekunden Hinzugekommenes nach, solange dieses Fenster sichtbar und der Strom nicht angehalten ist."
                    loading={initial.loading && eintraege.length === 0}
                    error={initial.error}
                    empty={!initial.loading && !initial.error && eintraege.length === 0}
                    height={560}
                    actions={
                        <button
                            type="button"
                            className={`btn btn-sm d-inline-flex align-items-center gap-2 ${pausiert ? "btn-outline-secondary" : "btn-outline-success"}`}
                            onClick={() => setPausiert((v) => !v)}
                        >
                            {pausiert ? <PiPlay aria-hidden="true" /> : <PiPause aria-hidden="true" />}
                            {pausiert ? "Fortsetzen" : "Anhalten"}
                            {!pausiert && <PiCircleFill className="nutzung-puls" size={8} color="#2f9e44" aria-hidden="true" />}
                        </button>
                    }
                >
                    <div style={{maxHeight: 500, overflowY: "auto"}}>
                        {eintraege.map((row) => (
                            <StromZeile key={ereignisSchluessel(row)} row={row} neu={neueSchluessel.has(ereignisSchluessel(row))} />
                        ))}
                    </div>
                </NutzungWidget>
            </NutzungLayout>
        </Container>
    );
}
